import React, { useEffect, useRef, type ReactNode, CSSProperties, memo } from "react"
import { motion } from "framer-motion"
import { useLiteModeContext } from "../../hooks/useLiteMode"

interface GlowCardProps {
  children: ReactNode
  className?: string
  glowColor?: "gold" | "blue" | "purple" | "green" | "red"
  size?: "sm" | "md" | "lg"
  width?: string | number
  height?: string | number
  customSize?: boolean
  onClick?: () => void
}

const glowColorMap = {
  gold: { base: 45, spread: 30 },    // Headman gold
  blue: { base: 220, spread: 200 },
  purple: { base: 280, spread: 300 },
  green: { base: 120, spread: 200 },
  red: { base: 0, spread: 200 },
}

const sizeMap = {
  sm: "w-48 h-64",
  md: "w-64 h-80",
  lg: "w-80 h-96",
}

const GlowCardComponent: React.FC<GlowCardProps> = ({
  children,
  className = "",
  glowColor = "gold",
  size = "md",
  width,
  height,
  customSize = false,
  onClick,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null)
  const { isLiteMode, prefersReducedMotion } = useLiteModeContext()

  // В lite mode отключаем pointer tracking для производительности
  useEffect(() => {
    if (isLiteMode || prefersReducedMotion) return

    const syncPointer = (e: PointerEvent) => {
      const { clientX: x, clientY: y } = e

      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect()
        const relX = x - rect.left
        const relY = y - rect.top
        cardRef.current.style.setProperty("--x", relX.toFixed(2))
        cardRef.current.style.setProperty("--y", relY.toFixed(2))
        cardRef.current.style.setProperty("--xp", (x / window.innerWidth).toFixed(2))
        cardRef.current.style.setProperty("--yp", (y / window.innerHeight).toFixed(2))
      }
    }

    document.addEventListener("pointermove", syncPointer)
    return () => document.removeEventListener("pointermove", syncPointer)
  }, [isLiteMode, prefersReducedMotion])

  const { base, spread } = glowColorMap[glowColor]

  const getSizeClasses = () => {
    if (customSize) return ""
    return sizeMap[size]
  }

  const getInlineStyles = (): CSSProperties => {
    const baseStyles: CSSProperties = {
      "--base": base,
      "--spread": spread,
      "--radius": "16",
      "--border": "1",
      "--backdrop": "rgba(14, 14, 14, 0.98)",
      "--backup-border": "rgba(255, 255, 255, 0.06)", // Minimal contrast border
      "--size": "280",
      "--outer": "1",
      "--border-size": "calc(var(--border, 1) * 1px)",
      "--spotlight-size": "calc(var(--size, 150) * 1px)",
      "--hue": "calc(var(--base) + (var(--xp, 0) * var(--spread, 0)))",
      backgroundImage: `
        linear-gradient(145deg, rgba(14,14,14,0.98) 0%, rgba(20,20,20,0.95) 100%),
        radial-gradient(
          var(--spotlight-size) var(--spotlight-size) at
          calc(var(--x, 50) * 1px)
          calc(var(--y, 50) * 1px),
          hsl(var(--hue, 45) 70% 50% / 0.06), transparent
        )
      `,
      backgroundColor: "var(--backdrop, transparent)",
      backgroundSize: "calc(100% + (2 * var(--border-size))) calc(100% + (2 * var(--border-size)))",
      backgroundPosition: "50% 50%",
      border: "var(--border-size) solid var(--backup-border)",
      position: "relative",
      touchAction: "pan-y",
    } as CSSProperties

    if (width !== undefined) {
      baseStyles.width = typeof width === "number" ? `${width}px` : width
    }
    if (height !== undefined) {
      baseStyles.height = typeof height === "number" ? `${height}px` : height
    }

    return baseStyles
  }

  return (
    <>
      <style>{`
        .glow-card-premium::before {
          content: "";
          position: absolute;
          inset: -1px;
          border-radius: inherit;
          background: radial-gradient(
            250px 250px at calc(var(--x, 50) * 1px) calc(var(--y, 50) * 1px),
            hsl(var(--base, 45) 80% 50% / 0.5),
            transparent 70%
          );
          opacity: 0;
          transition: opacity 0.4s ease;
          pointer-events: none;
          z-index: -1;
        }
        .glow-card-premium:hover::before {
          opacity: 1;
        }
        .glow-card-premium::after {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: radial-gradient(
            180px 180px at calc(var(--x, 50) * 1px) calc(var(--y, 50) * 1px),
            hsl(var(--base, 45) 80% 70% / 0.12),
            transparent 60%
          );
          pointer-events: none;
        }
      `}</style>
      <motion.div
        ref={cardRef}
        style={getInlineStyles()}
        whileHover={isLiteMode || prefersReducedMotion ? {} : { y: -4, scale: 1.02 }}
        whileTap={onClick && !isLiteMode ? { scale: 0.98 } : {}}
        transition={{ duration: 0.2, ease: "easeOut" }}
        onClick={onClick}
        className={`
          ${!isLiteMode ? 'glow-card-premium' : ''}
          ${getSizeClasses()}
          rounded-2xl
          relative
          p-4
          ${!isLiteMode ? 'backdrop-blur-md' : ''}
          transition-colors duration-200
          ${!isLiteMode ? 'hover:shadow-[0_0_40px_rgba(250,204,21,0.18)] hover:border-amber-500/30' : ''}
          ${onClick ? 'cursor-pointer' : ''}
          ${className}
        `}
      >
        {children}
      </motion.div>
    </>
  )
}

// Мемоизируем для предотвращения лишних ре-рендеров
export const GlowCard = memo(GlowCardComponent)

